interface UseDragProps {
  type: 'share' | 'project'
  identifier: string
}

/**
 * Hook that exposes attributes to drag an element, accepts dragging both
 * shares or projects (includes the shareId or projectId in the event.dataTransfer data)
 */
export const useDrag = ({ type, identifier }: UseDragProps) => {
  const onDragStart = (event: React.DragEvent<HTMLElement>) => {
    event.stopPropagation()

    /** Set data-being-dragged to true so we know which element started the drag */
    /** This value is later reset in onDragEnd (down below) */
    const element = event.target as HTMLElement
    element.dataset.beingDragged = 'true'

    const stringifiedParameters =
      type === 'project'
        ? JSON.stringify({ projectId: identifier })
        : JSON.stringify({ shareId: identifier })

    event.dataTransfer.setData('text', stringifiedParameters)

    if (!(event.target instanceof Element)) {
      // Typescript will complain if we try to use `event.target.querySelector`
      // without first refining the type by asserting that `event.target` is an
      // `Element`
      return
    }

    // Set a custom drag image if one is specified
    const dragImage = event.target.querySelector('[data-drag-image]')
    if (dragImage) {
      event.dataTransfer.setDragImage(dragImage, 0, 0)
    }
  }

  const onDragEnd = (event: React.DragEvent<HTMLElement>) => {
    /** Reset the data-being-dragged attribute  */
    const element = event.target as HTMLElement
    element.dataset.beingDragged = 'false'
  }

  return {
    onDragStart,
    onDragEnd,
  }
}
